import React from 'react';
import { Box, Typography, Paper, TextField } from '@mui/material';

/**
 * Format a resolved field value for display
 */
function formatValue(value, dataType) {
  if (value === null || value === undefined) {
    return 'Not specified';
  }

  switch (dataType) {
    case 'date':
    case 'datetime':
      return new Date(value).toLocaleString();
    case 'boolean':
      return value ? 'Yes' : 'No';
    case 'number':
      return value.toString();
    case 'object':
    case 'array':
      // Format objects/arrays as JSON
      return JSON.stringify(value, null, 2);
    default:
      // Handle objects that aren't explicitly typed as object
      if (typeof value === 'object') {
        return JSON.stringify(value, null, 2);
      }
      return String(value);
  }
}

/**
 * Render a single template field (read-only or editable)
 */
const TemplateField = ({ field, formData, onFieldChange }) => {
  const {
    tag_id,
    resolved_label,
    resolved_value,
    resolved_source,
    resolved_data_type,
    required
  } = field;

  // Determine if field is editable (witness/narrative fields)
  const isEditable = resolved_source === 'witness' || tag_id.startsWith('narrative.');

  // For editable fields, show input
  if (isEditable) {
    // Extract section and field name from tag_id
    const parts = tag_id.split('.');
    const section = parts[0]; // 'narrative', 'assertions', etc.
    const fieldName = parts.slice(1).join('.');

    // Get current value from formData
    let currentValue = '';
    if (section === 'narrative' && formData?.narrative) {
      currentValue = formData.narrative[fieldName] || '';
    }

    const isMultiline = fieldName === 'account' || resolved_data_type === 'text';

    return (
      <TextField
        fullWidth
        label={resolved_label || tag_id}
        value={currentValue}
        onChange={(e) => onFieldChange && onFieldChange(section, fieldName, e.target.value)}
        margin="normal"
        required={required}
        multiline={isMultiline}
        rows={isMultiline ? 6 : 1}
      />
    );
  }

  // For read-only system/computed fields
  const isJsonValue = typeof resolved_value === 'object' && resolved_value !== null;

  // Render key-value pairs for objects instead of raw JSON
  if (isJsonValue) {
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {resolved_label || tag_id}
        </Typography>
        <Box sx={{ pl: 2 }}>
          {Object.entries(resolved_value).map(([key, value]) => (
            <Box key={key} sx={{ mb: 0.5 }}>
              <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:
              </Typography>{' '}
              <Typography variant="body2" component="span">
                {typeof value === 'object' && value !== null
                  ? JSON.stringify(value, null, 2)
                  : String(value)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  // For simple values
  const formattedValue = formatValue(resolved_value, resolved_data_type);

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {resolved_label || tag_id}
      </Typography>
      <Typography variant="body1">
        {formattedValue}
      </Typography>
    </Box>
  );
};

/**
 * Render a template section with all its fields
 */
const TemplateSection = ({ section, formData, onFieldChange }) => {
  if (!section.fields || section.fields.length === 0) {
    return null;
  }

  // Group fields by source
  const systemFields = section.fields.filter(f => f.resolved_source === 'system');
  const witnessFields = section.fields.filter(f => f.resolved_source === 'witness' || f.tag_id.startsWith('narrative.'));
  const computedFields = section.fields.filter(f => f.resolved_source === 'computed');

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" gutterBottom>
        {section.title}
      </Typography>
      {section.description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {section.description}
        </Typography>
      )}

      {/* System fields - read-only, grey background */}
      {systemFields.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle2" gutterBottom color="text.secondary">
            System Recorded
          </Typography>
          {systemFields.map(field => (
            <TemplateField
              key={field.tag_id}
              field={field}
              formData={formData}
              onFieldChange={onFieldChange}
            />
          ))}
        </Paper>
      )}

      {/* Computed fields - read-only */}
      {computedFields.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom color="text.secondary">
            System Calculated
          </Typography>
          {computedFields.map(field => (
            <TemplateField
              key={field.tag_id}
              field={field}
              formData={formData}
              onFieldChange={onFieldChange}
            />
          ))}
        </Paper>
      )}

      {/* Witness fields - editable */}
      {witnessFields.length > 0 && (
        <Box>
          {witnessFields.map(field => (
            <TemplateField
              key={field.tag_id}
              field={field}
              formData={formData}
              onFieldChange={onFieldChange}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

/**
 * Main component: Renders template with resolved field values
 */
const TemplateRenderer = ({ statement, formData, onFieldChange }) => {
  const template = statement?.draft_data?.template_snapshot;

  if (!template?.config?.sections) {
    return (
      <Typography variant="body2" color="error">
        No template configuration found
      </Typography>
    );
  }

  return (
    <Box>
      {template.config.sections.map((section, index) => (
        <TemplateSection
          key={index}
          section={section}
          formData={formData}
          onFieldChange={onFieldChange}
        />
      ))}
    </Box>
  );
};

export default TemplateRenderer;
